import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment/min/moment-with-locales';
import { isVietnam } from 'Util/DataUtils';
import IntlMessages from 'Util/IntlMessages';
import YearDropdown from './YearDropdown';
import SearchInput from './SearchInput';
import { useIntl } from 'react-intl';
import { useLocation, useNavigate } from 'react-router-dom';
import { collapsedFieldSidebarAction, setCountry, setLanguage } from 'Actions/AppSettingsActions';
import AppConfig from 'Constants/AppConfig';
import icArrow from 'Assets/img/ic_arrow_right_16.png';
import icArrow2 from 'Assets/img/ic_arrow_right_16@2x.png';
import icArrow3 from 'Assets/img/ic_arrow_right_16@3x.png';
import { getImageSet } from 'Util/DataUtils';
import FieldItem from './FieldItem';
import useFilter from '../hooks/useFilter';

const arrows = getImageSet([icArrow, icArrow2, icArrow3]);

const VI_TITLE_CLICK_RESET_MS = 10000;
const VI_TITLE_CLICKS_TO_SWITCH_KO = 10;

const BasicSection = ({ setOpenUpdatePopup, setFieldId, goToEdit }) => {
  const { user } = useSelector(state => state.authUser);
  const { allFieldList, defaultField } = useSelector(state => state.config);
  const { locale: appLocale } = useSelector(state => state.settings);
  const { formatMessage } = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { searchText, updateText, getFilteredList, yearList, selectedYear, updateYear } = useFilter({ fieldList: allFieldList });
  const viTitleClickCountRef = useRef(0);
  const viTitleClickResetTimerRef = useRef(null);

  const handleViTitleClick = useCallback(() => {
    if (appLocale?.locale !== 'vi') return;
    if (viTitleClickResetTimerRef.current) {
      clearTimeout(viTitleClickResetTimerRef.current);
    }
    viTitleClickCountRef.current += 1;
    if (viTitleClickCountRef.current >= VI_TITLE_CLICKS_TO_SWITCH_KO) {
      viTitleClickCountRef.current = 0;
      const applyKoLocale = () => {
        dispatch(setCountry('ko'));
        dispatch(setLanguage(AppConfig.languages.ko));
        moment.locale('ko');
      };
      queueMicrotask(applyKoLocale);
      return;
    }
    viTitleClickResetTimerRef.current = setTimeout(() => {
      viTitleClickCountRef.current = 0;
      viTitleClickResetTimerRef.current = null;
    }, VI_TITLE_CLICK_RESET_MS);
  }, [appLocale?.locale, dispatch]);

  useEffect(() => {
    if (appLocale?.locale !== 'vi') {
      viTitleClickCountRef.current = 0;
      if (viTitleClickResetTimerRef.current) {
        clearTimeout(viTitleClickResetTimerRef.current);
        viTitleClickResetTimerRef.current = null;
      }
    }
  }, [appLocale?.locale]);

  useEffect(() => {
    return () => {
      if (viTitleClickResetTimerRef.current) {
        clearTimeout(viTitleClickResetTimerRef.current);
      }
    };
  }, []);

  const isDashboard = location.pathname.indexOf('admin/dashboard') > -1;
  const isNotAll = location.pathname.indexOf('dashboard/field') > -1;

  const isFirstField = useMemo(() => {
    return defaultField?.isFirstField;
  }, [defaultField]);

  return (
    <>
      <div className='field-sidebar-fixed' data-field-toggle='true'>
        <div className='field-sidebar-header'>
          <span className={`field-sidebar-title ${isVietnam(user) && 'vi'} no-drag`} onClick={handleViTitleClick}>
            <IntlMessages id='sidebar.field.title' />
          </span>
          {!isFirstField && (
            <span
              className='field-sidebar-edit-button no-drag cursor-pointer'
              onClick={() => {
                goToEdit();
              }}
            >
              <IntlMessages id='sidebar.field.edit' />
            </span>
          )}
        </div>
        <div className='field-sidebar-create-button-container'>
          <button className='field-sidebar-create-button no-drag cursor-pointer' onClick={() => setOpenUpdatePopup(true)}>
            <IntlMessages id='sidebar.field.create' />
          </button>
        </div>
        <div style={{ width: '100%', height: '1px', backgroundColor: '#eee' }} />
        {!isFirstField && (
          <div className='field-sidebar-filter-container'>
            {yearList.length > 1 && <YearDropdown options={yearList} value={selectedYear} onChange={updateYear} />}
            <SearchInput text={searchText} onChangeText={e => updateText(e.target.value)} />
          </div>
        )}
      </div>
      <div className='field-sidebar-list'>
        {searchText && getFilteredList.length === 0 ? (
          <div className='field-sidebar-list-empty'>
            &apos;<span className='field-sidebar-list-empty-highlight'>{searchText}</span>&apos;{formatMessage({ id: 'sidebar.field.empty.list' })}
          </div>
        ) : (
          <>
            {isDashboard && !user.isStaff && (
              <div
                className={`field-sidebar-list-item cursor-pointer ${isNotAll ? '' : 'default-item'}`}
                style={{ padding: '15px', height: '45px' }}
                onClick={() => {
                  if (isNotAll) {
                    navigate('/admin/dashboard');
                    dispatch(collapsedFieldSidebarAction(true));
                  }
                }}
              >
                <div className='item-name' style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div className='item-name-text'>{formatMessage({ id: 'field.all' })}</div>
                  <img {...arrows} alt='arrow' />
                </div>
              </div>
            )}
            {getFilteredList.map(field => (
              <FieldItem
                key={field._id}
                field={field}
                openUpdateField={id => {
                  setFieldId(id);
                  setOpenUpdatePopup(true);
                }}
                openCreateField={() => {
                  setOpenUpdatePopup(true);
                }}
                isAll={isDashboard && !isNotAll}
                searchText={searchText}
              />
            ))}
          </>
        )}
      </div>
    </>
  );
};

export default BasicSection;
